import { useTranslation } from "react-i18next";

import {
  type CcDraftStateItem,
  type CcDraftStatus,
  type CcDraftingSummary,
  isDraftHandoff,
} from "@/api/code-companion";
import { tp } from "@/lib/plural";

/**
 * A fase em que o run monta o change set (CC.34 F2/F3).
 *
 * Antes desta fatia o botão "Iniciar desenvolvimento" exigia change set montado, e
 * montar change set era operação de MCP: o dev abria o Claude, draftava objeto a objeto,
 * e só então a tela servia para alguma coisa. Agora o run drafta o pacote inteiro, e a
 * consequência para a tela é que existe uma fase longa — uma chamada ao modelo por
 * objeto — em que **nada aparece na matriz**, porque os objetos ainda não existem no
 * change set. Sem este painel, os primeiros minutos de um run de 33 objetos são
 * indistinguíveis de um run travado.
 *
 * **A palavra importa mais que o número.** `needs_redesign` e `unsupported` não são
 * falha: são entrega a uma pessoa. O objeto sai do lote autônomo porque o alvo recusa a
 * dependência sem oferecer sucessor, ou porque o tipo não tem caminho automático — e nos
 * dois casos o destino é um dev com o Claude no modo assistido, que é onde o produto tem
 * a resposta. Chamar isso de "falhou" manda a pessoa procurar bug onde há veredito.
 */

/** Uma barra empilhada não mente sobre denominador: as partes somam o total. */
type DraftSegment = {
  key: "drafted" | "handoff" | "failed" | "pending";
  n: number;
  color: string;
};

export function draftSegments(s: CcDraftingSummary): DraftSegment[] {
  // O literal precisa do tipo: sem ele o `key` infere `string`, e o `.filter` devolve
  // um array que nao satisfaz o retorno. `bun run typecheck` pega; `tsc --noEmit` na
  // raiz nao, porque o tsconfig de la tem `files: []` e nao checa nada.
  const segmentos: DraftSegment[] = [
    { key: "drafted", n: s.drafted, color: "#2b8f57" },
    // Âmbar, e não vermelho: espera gente, não quebrou.
    { key: "handoff", n: s.handoff, color: "#c98a12" },
    { key: "failed", n: s.failed, color: "#b02a31" },
    { key: "pending", n: s.pending, color: "#c9ccd1" },
  ];
  return segmentos.filter((seg) => seg.n > 0);
}

/** Percentual resolvido: tudo que já tem destino, incluindo o que espera um dev. */
export function draftResolvedPct(s: CcDraftingSummary): number {
  if (!s.total) return 0;
  return Math.round((100 * (s.total - s.pending)) / s.total);
}

const STATUS_TONE: Record<CcDraftStatus, string> = {
  drafted: "#2b8f57",
  needs_redesign: "#c98a12",
  unsupported: "#c98a12",
  failed: "#b02a31",
  pending: "#6b7280",
};

export default function DraftingProgress({
  summary,
  items,
  compact = false,
}: {
  summary: CcDraftingSummary | null | undefined;
  items?: CcDraftStateItem[];
  /** No painel do run só a barra; na tela do pacote, a lista do que espera um dev. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  // Roster vazio é estado válido (pacote que nunca rodou), e desenhar uma barra de zero
  // seria inventar informação.
  if (!summary || !summary.total) return null;

  const segmentos = draftSegments(summary);
  const paraHumano = (items ?? []).filter((i) => isDraftHandoff(i.status));

  return (
    <div data-testid="drafting-progress" className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {t("Drafting")}
        </span>
        <span>
          {t("{{done}} of {{total}} objects transformed", {
            done: summary.drafted,
            total: summary.total,
          })}
        </span>
        <div className="flex h-1.5 w-40 overflow-hidden rounded-full bg-muted">
          {segmentos.map((seg) => (
            <div
              key={seg.key}
              data-testid={`drafting-seg-${seg.key}`}
              style={{ width: `${(100 * seg.n) / summary.total}%`, background: seg.color }}
            />
          ))}
        </div>
        {summary.pending > 0 && (
          <span className="text-muted-foreground">
            {tp(t, summary.pending, "{{n}} to go", "{{n}} to go")}
          </span>
        )}
      </div>

      {/* A frase que evita o diagnóstico errado. Ela aparece mesmo em `compact`, porque
          é a informação que muda o que a pessoa faz a seguir. */}
      {summary.handoff > 0 && (
        <div
          data-testid="drafting-handoff"
          className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-200"
        >
          <strong className="font-semibold">
            {tp(
              t,
              summary.handoff,
              "1 object needs a dev with Claude",
              "{{n}} objects need a dev with Claude",
            )}
          </strong>{" "}
          <span className="opacity-90">
            {t(
              "The target refuses these dependencies without offering a released successor, or the type has no automatic path. They are not failures: open them in assisted mode.",
            )}
          </span>

          {!compact && paraHumano.length > 0 && (
            <ul className="mt-2 space-y-1">
              {paraHumano.map((i) => (
                <li key={i.object_name} className="flex flex-wrap items-baseline gap-2">
                  <span className="font-mono text-[11px] font-semibold">{i.object_name}</span>
                  <span className="font-mono text-[10px] uppercase opacity-70">
                    {i.source_type}
                    {i.target_type && i.target_type !== i.source_type && ` → ${i.target_type}`}
                  </span>
                  {i.last_error && <span className="opacity-80">{i.last_error}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Falha de verdade é a que a retentativa resolve, e por isso fica separada. */}
      {summary.failed > 0 && (
        <div
          data-testid="drafting-failed"
          className="rounded-md border border-red-200 bg-red-50/60 px-3 py-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/20 dark:text-red-300"
        >
          {tp(
            t,
            summary.failed,
            "1 object could not be transformed",
            "{{n}} objects could not be transformed",
          )}{" "}
          {t("Retrying the run picks them up again.")}
        </div>
      )}

      {!compact && items && items.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">
            {t("See every object")}
          </summary>
          <ul className="mt-2 space-y-1">
            {items.map((i) => (
              <li key={i.object_name} className="flex flex-wrap items-baseline gap-2">
                <span
                  className="font-mono text-[10px] font-semibold uppercase"
                  style={{ color: STATUS_TONE[i.status] }}
                >
                  {i.status}
                </span>
                <span className="font-mono text-[11px]">{i.object_name}</span>
                {i.target_name && i.target_name !== i.object_name && (
                  <span className="font-mono text-[10px] text-muted-foreground">
                    → {i.target_name}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
